import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { GoogleLogo, MicrosoftOutlookLogo, Key, Plus, Trash, Copy, BookOpen, Robot, FloppyDisk, Brain } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { WhatsAppCard } from "@/components/WhatsAppCard";
import { AgentLanguageCard } from "@/components/AgentLanguageCard";
import { PrelearnCard } from "@/components/PrelearnCard";
import { CategoriesCard } from "@/components/CategoriesCard";
import { WebhooksCard } from "@/components/WebhooksCard";
import api, { errDetail } from "@/lib/api";
import { ModelPicker } from "@/components/ModelPicker";

export default function SettingsPage() {
  const [keys, setKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState({ read: true, send: true, agent: true, mcp: true });
  const [newKeyCap, setNewKeyCap] = useState("");
  const [auditFor, setAuditFor] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [llm, setLlm] = useState(null);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [connections, setConnections] = useState([]);
  const [swapAcct, setSwapAcct] = useState(null);
  const [swapText, setSwapText] = useState("");
  const [autoLearn, setAutoLearn] = useState(null);
  const [lockedModel, setLockedModel] = useState(null);
  const [otherEmails, setOtherEmails] = useState([]);
  const [otherInput, setOtherInput] = useState("");
  const loadAutoLearn = useCallback(() => api.get("/me/auto-learn").then((r) => setAutoLearn(r.data)).catch(() => {}), []);
  const loadKeys = useCallback(() => api.get("/apikeys").then((r) => setKeys(r.data)), []);
  const loadConnections = useCallback(() => api.get("/connections").then((r) => setConnections(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadKeys();
    loadConnections();
    loadAutoLearn();
    api.get("/team/tokens").then((r) => setLockedModel(r.data.locked_model || null)).catch(() => {});
    api.get("/me/other-emails").then((r) => setOtherEmails(r.data.other_emails || [])).catch(() => {});
    api.get("/llm/available").then((r) => {
      setLlm(r.data);
      const configured = r.data.providers.filter((p) => p.configured);
      setProvider(r.data.preferred_provider || configured[0]?.provider || "openai");
      setModel(r.data.preferred_model || "");
    }).catch(() => {});
  }, [loadKeys]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "gmail") {
      toast.success("Gmail connected! Importing your mailbox — this takes a few seconds…");
      window.history.replaceState({}, "", "/settings");
      loadConnections();
    } else if (params.get("connect_error")) {
      toast.error(`Gmail connection failed: ${params.get("connect_error")} — please try again`);
      window.history.replaceState({}, "", "/settings");
    }
  }, [loadConnections]);

  const toggleAutoLearn = async () => {
    try {
      const next = !autoLearn.enabled;
      await api.put("/me/auto-learn", { enabled: next });
      toast.success(next ? t("settings.autolearn_on", "Auto-learning enabled") : t("settings.autolearn_off", "Auto-learning paused"));
      loadAutoLearn();
    } catch (e) { toast.error(errDetail(e)); }
  };


  const connect = async (provider, force = false) => {
    if (provider === "gmail" && !force) {
      const existing = connections.find((c) => c.provider === "gmail" && c.status !== "reauth_required");
      if (existing) { setSwapAcct(existing); setSwapText(""); return; }
    }
    if (provider === "gmail") {
      try {
        const { data } = await api.get("/oauth/gmail/start?from_page=settings");
        window.location.href = data.url;
      } catch (e) { toast.error(errDetail(e)); }
      return;
    }
    try {
      const { data } = await api.post("/connections/connect", { provider });
      if (data.configured) toast.success(data.message);
      else toast.info(data.message, { duration: 8000 });
    } catch (e) { toast.error(errDetail(e)); }
  };

  const disconnect = async (acct) => {
    if (!window.confirm(t("settings.disc_warn", "Disconnecting will also remove all synced emails and learned data for this account. Continue?"))) return;
    try { await api.delete(`/connections/${acct.id}`); toast.success(t("settings.disconnected", "Disconnected")); loadConnections(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const saveOtherEmails = async (list) => {
    try {
      const { data } = await api.put("/me/other-emails", { other_emails: list });
      setOtherEmails(data.other_emails);
      toast.success(t("pi.other_saved", "Addresses saved"));
    } catch (e) { toast.error(errDetail(e)); }
  };
  const addOtherEmail = () => {
    const v = otherInput.trim().toLowerCase();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error(t("pi.other_invalid", "That doesn't look like an email address")); return; }
    if (otherEmails.includes(v)) { setOtherInput(""); return; }
    if (otherEmails.length >= 10) { toast.error(t("pi.other_max", "Maximum 10 addresses")); return; }
    setOtherInput("");
    saveOtherEmails([...otherEmails, v]);
  };

  const createKey = async () => {
    if (!newKeyName.trim()) { toast.error(t("settings.key_name_req", "Give the key a name")); return; }
    const scopes = Object.keys(newKeyScopes).filter((s) => newKeyScopes[s]);
    if (scopes.length === 0) { toast.error(t("settings.scope_req", "Pick at least one scope")); return; }
    try {
      const { data } = await api.post("/apikeys", { name: newKeyName, scopes, daily_token_cap: newKeyCap ? parseInt(newKeyCap, 10) : null });
      toast.success(t("settings.key_created", "API key created — copy it now!"));
      setNewKeyName(""); setNewKeyCap(""); loadKeys();
      navigator.clipboard?.writeText(data.key).catch(() => {});
    } catch (e) { toast.error(errDetail(e)); }
  };
  const toggleAudit = async (k) => {
    if (auditFor === k.id) { setAuditFor(null); setAuditData(null); return; }
    try { const { data } = await api.get(`/apikeys/${k.id}/audit`); setAuditFor(k.id); setAuditData(data); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const revoke = async (k) => {
    try { await api.delete(`/apikeys/${k.id}`); toast.success(t("settings.key_revoked", "Key revoked")); loadKeys(); } catch (e) { toast.error(errDetail(e)); }
  };

  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

  return (
    <PageShell title={t("settings.title", "Settings")} subtitle={t("settings.subtitle", "Choose your AI model, connect email providers and rent your inbox to third-party applications.")} testId="settings-page">
      <WhatsAppCard />
      <CategoriesCard />
      <PrelearnCard />
      <AgentLanguageCard />
      <h2 className="font-heading font-bold text-gray-900 mb-1 flex items-center gap-2"><Robot size={18} className="text-blue-600" /> {t("settings.ai_model_title", "AI Model for your Email Agent")}</h2>
      {lockedModel ? (
        <div data-testid="model-locked-notice" className="border border-gray-200 rounded-md p-4 mb-8 text-sm text-gray-600">
          {t("settings.locked_notice", "🔒 Your plan uses a fixed model: {model}. Upgrade your plan to choose any model.", { model: `${lockedModel.provider} / ${lockedModel.model}` })}
        </div>
      ) : (<>
      <p className="text-sm text-gray-500 mb-4">{t("settings.pick_provider2", "Search and pick any model — prices shown per 1M tokens. \"Auto\" lets the system route each message to the best model.")}</p>
      <div className="border border-gray-200 rounded-md p-4 mb-8">
        <label className="text-xs font-medium text-gray-600 block mb-1">{t("settings.model", "Model")}</label>
        <ModelPicker value={model} onSaved={(m) => { setModel(m.id); setProvider(m.provider); }} />
        {llm && llm.providers.every((p) => !p.configured) && (
          <p data-testid="provider-not-configured-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-3">
            {t("settings.no_keys_warn", "The super admin hasn't added any AI provider key yet — your agent runs in built-in demo mode until then.")}
          </p>
        )}
      </div>
      </>)}
      <h2 className="font-heading font-bold text-gray-900 mb-1 flex items-center gap-2"><Brain size={18} className="text-purple-600" /> {t("settings.memory_title", "Memory auto-learning")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("settings.memory_desc", "After each email you send, the agent quietly learns durable facts (preferences, decisions, deadlines) about your contacts.")}</p>
      <div className="border border-gray-200 rounded-md p-4 mb-8" data-testid="auto-learn-card">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">{t("settings.autolearn", "Auto-learn from sent emails")}</span>
          <button data-testid="auto-learn-toggle" onClick={toggleAutoLearn}
            className={`relative w-10 h-6 rounded-full transition-colors ${autoLearn?.enabled ? "bg-blue-600" : "bg-gray-300"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${autoLearn?.enabled ? "left-[18px]" : "left-0.5"}`} />
          </button>
        </div>
        {autoLearn && (
          <div className="mt-3" data-testid="auto-learn-usage">
            {autoLearn.cap == null ? (
              <p className="text-xs text-gray-500">{t("settings.today_learned_unl", "Today: learned from {n} emails", { n: autoLearn.today_count })} · <span className="font-semibold text-emerald-700">{t("settings.unlimited", "Unlimited")}</span>{" "}{autoLearn.package_name ? t("settings.on_plan", "on your {plan} plan", { plan: autoLearn.package_name }) : t("settings.on_plan_generic", "on your plan")}</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-1.5">{t("settings.today_learned", "Today: learned from {n} of {cap} emails", { n: autoLearn.today_count, cap: autoLearn.cap })}{autoLearn.package_name ? ` ${t("settings.daily_limit_plan", "(daily limit set by your {plan} plan)", { plan: autoLearn.package_name })}` : ""}</p>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, (autoLearn.today_count / Math.max(1, autoLearn.cap)) * 100)}%` }} />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <h2 className="font-heading font-bold text-gray-900 mb-3">{t("settings.providers", "Email provider connections")}</h2>
      {connections.length > 0 && (
        <div className="space-y-2 mb-4">
          {connections.map((c) => (
            <div key={c.id} data-testid={`connection-row-${c.provider}`} className="border border-gray-200 rounded-md p-3 flex items-center gap-3">
              {c.provider === "gmail" ? <GoogleLogo size={18} weight="bold" className="text-red-500" /> : <MicrosoftOutlookLogo size={18} weight="bold" className="text-blue-500" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{c.email}</div>
                <div className="text-xs text-gray-400">{c.provider} · {t("settings.connected_on", "connected")} {c.connected_at?.slice(0, 10)}{c.last_synced_at ? ` · ${t("settings.synced", "synced")} ${c.last_synced_at.slice(11, 16)} UTC` : ""}</div>
              </div>
              {c.status === "reauth_required" ? (
                <button data-testid={`reconnect-${c.provider}`} onClick={() => connect(c.provider)}
                  className="text-xs font-semibold bg-amber-100 text-amber-800 rounded-full px-3 py-1">{t("settings.reconnect", "Reconnect")}</button>
              ) : (
                <span data-testid={`connection-status-${c.provider}`} className="text-xs font-semibold bg-green-100 text-green-700 rounded-full px-3 py-1">{t("settings.connected", "Connected")}</span>
              )}
              <button data-testid={`disconnect-${c.provider}`} onClick={() => disconnect(c)}
                className="text-gray-400 hover:text-red-600 p-1"><Trash size={15} /></button>
            </div>
          ))}
        </div>
      )}
      {swapAcct && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="gmail-swap-modal">
          <div className="bg-white rounded-lg shadow-xl p-5 w-[460px]">
            <h3 className="font-heading font-bold text-gray-900 mb-2">{t("settings.swap_title", "Replace connected Gmail?")}</h3>
            <p className="text-sm text-gray-600 mb-3">{t("settings.swap_body", "You already have {email} connected. Connecting a different account will permanently delete all its synced emails, categories and drafts. Type the current address to confirm.").replace("{email}", swapAcct.email || "")}</p>
            <input data-testid="gmail-swap-confirm-input" value={swapText} onChange={(e) => setSwapText(e.target.value)}
              placeholder={t("settings.swap_ph", "Type the current email address")}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3" />
            <div className="flex justify-end gap-2">
              <button data-testid="gmail-swap-cancel" onClick={() => setSwapAcct(null)}
                className="text-sm text-gray-500 hover:text-gray-800 px-3 py-1.5">{t("settings.swap_cancel", "Cancel")}</button>
              <button data-testid="gmail-swap-continue"
                disabled={swapText.trim().toLowerCase() !== (swapAcct.email || "").toLowerCase()}
                onClick={() => { setSwapAcct(null); connect("gmail", true); }}
                className="text-sm font-semibold bg-red-600 text-white rounded-md px-3 py-1.5 disabled:opacity-40">{t("settings.swap_continue", "Delete data & continue")}</button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <div className="border border-gray-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2"><GoogleLogo size={20} weight="bold" className="text-red-500" /><span className="font-semibold text-sm">Gmail</span></div>
          <p className="text-xs text-gray-500 mb-3">{t("settings.gmail_desc", "Read, send & organize your Gmail via the Gmail API (OAuth 2.0).")}</p>
          <button data-testid="connect-gmail-btn" onClick={() => connect("gmail")}
            className="w-full border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2 transition-colors">{t("settings.connect_gmail", "Connect Gmail")}</button>
        </div>
        <div className="border border-gray-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-2"><MicrosoftOutlookLogo size={20} weight="bold" className="text-blue-500" /><span className="font-semibold text-sm">Outlook</span></div>
          <p className="text-xs text-gray-500 mb-3">{t("settings.outlook_desc", "Connect Outlook / Microsoft 365 via Microsoft Graph API.")}</p>
          <button data-testid="connect-outlook-btn" onClick={() => connect("outlook")}
            className="w-full border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md py-2 transition-colors">{t("settings.connect_outlook", "Connect Outlook")}</button>
        </div>
      </div>
      <Link to="/docs/oauth" data-testid="oauth-docs-link" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 mb-8">
        <BookOpen size={15} /> {t("settings.oauth_docs", "How to set up Gmail & Outlook OAuth credentials →")}
      </Link>

      <h2 className="font-heading font-bold text-gray-900 mb-1">{t("pi.other_title", "My other email addresses")}</h2>
      <p className="text-sm text-gray-500 mb-1">{t("pi.other_desc", "Add work aliases or secondary addresses (max 10). The Priority Inbox uses them to know when a message is really addressed to you.")}</p>
      <p data-testid="priority-learning-note" className="text-xs text-gray-400 mb-4">{t("pi.learn_note", "Learning works even if you handle mail in Gmail directly — the app reads the outcome, not the clicks.")}</p>
      <div className="border border-gray-200 rounded-md p-4 mb-8" data-testid="other-emails-card">
        {otherEmails.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="other-emails-chips">
            {otherEmails.map((e) => (
              <span key={e} data-testid={`other-email-chip-${e}`} className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium rounded-full px-3 py-1">
                {e}
                <button data-testid={`other-email-remove-${e}`} onClick={() => saveOtherEmails(otherEmails.filter((x) => x !== e))}
                  className="text-blue-400 hover:text-red-600 font-bold leading-none">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="other-email-input" value={otherInput} onChange={(e) => setOtherInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOtherEmail(); } }}
            placeholder={t("pi.other_ph", "name@company.com — press Enter")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button data-testid="other-email-add-btn" onClick={addOtherEmail}
            className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-md px-4 transition-colors"><Plus size={14} /> {t("pi.other_add", "Add")}</button>
        </div>
      </div>

      <h2 className="font-heading font-bold text-gray-900 mb-1 mt-4">{t("settings.api_service", "API Service (rent to third-party apps)")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("settings.api_desc", "Generate API keys so external applications can use your inbox and agent programmatically.")}</p>
      <div className="border border-gray-200 rounded-md p-4 mb-4">
        <div className="flex gap-2 mb-3">
          <input data-testid="apikey-name-input" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder={t("settings.key_name_ph", "Key name (e.g. My CRM app)")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button data-testid="apikey-create-btn" onClick={createKey}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 transition-colors"><Plus size={14} /> {t("settings.generate_key", "Generate key")}</button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-medium text-gray-500">{t("settings.scopes", "Scopes:")}</span>
          {["read", "send", "agent", "mcp"].map((s) => (
            <label key={s} data-testid={`apikey-scope-${s}`} className={`flex items-center gap-1 text-xs font-semibold uppercase rounded-full border px-3 py-1 cursor-pointer transition-colors ${newKeyScopes[s] ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
              <input type="checkbox" className="hidden" checked={!!newKeyScopes[s]} onChange={() => setNewKeyScopes({ ...newKeyScopes, [s]: !newKeyScopes[s] })} />
              {newKeyScopes[s] ? "✓ " : ""}{s}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500">{t("settings.cap_label", "Daily token limit (optional):")}</label>
          <input data-testid="apikey-cap-input" type="number" min="0" value={newKeyCap} onChange={(e) => setNewKeyCap(e.target.value)} placeholder="e.g. 2000"
            className="w-28 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <span className="text-[11px] text-gray-400">{t("settings.no_cap_hint", "No limit = a buggy external script could spend your whole token pool.")}</span>
        </div>
      </div>
      <div className="space-y-2 mb-6">
        {keys.map((k) => {
          const today = new Date().toISOString().slice(0, 10);
          const used = k.tokens_day === today ? (k.tokens_today || 0) : 0;
          return (
          <div key={k.id} data-testid={`apikey-row-${k.id}`} className="border border-gray-200 rounded-md p-3">
            <div className="flex items-center gap-3">
              <Key size={16} className="text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
                  {k.name}
                  {(k.scopes || ["read", "send", "agent", "mcp"]).map((s) => (
                    <span key={s} data-testid={`apikey-scope-badge-${k.id}-${s}`} className="text-[10px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">{s}</span>
                  ))}
                </div>
                <code className="text-xs text-gray-500 break-all">{k.key}</code>
              </div>
              <span className="text-xs text-gray-400 shrink-0">{k.requests_count} {t("settings.calls", "calls")}</span>
              <button data-testid={`apikey-audit-${k.id}`} onClick={() => toggleAudit(k)}
                className="text-xs font-medium text-gray-400 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 transition-colors">{t("settings.audit", "Audit")}</button>
              <button data-testid={`apikey-copy-${k.id}`} onClick={() => { navigator.clipboard?.writeText(k.key); toast.success(t("common.copied", "Copied")); }} className="text-gray-400 hover:text-gray-700 p-1"><Copy size={15} /></button>
              <button data-testid={`apikey-revoke-${k.id}`} onClick={() => revoke(k)} className="text-gray-400 hover:text-red-600 p-1"><Trash size={15} /></button>
            </div>
            {k.daily_token_cap ? (
              <div className="mt-2" data-testid={`apikey-usage-${k.id}`}>
                <p className="text-xs text-gray-500 mb-1">{used.toLocaleString()} / {k.daily_token_cap.toLocaleString()} {t("settings.tokens_today", "tokens today")}</p>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${used >= k.daily_token_cap ? "bg-red-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, (used / Math.max(1, k.daily_token_cap)) * 100)}%` }} />
                </div>
              </div>
            ) : (
              <p className="mt-2 text-xs text-gray-400" data-testid={`apikey-nocap-${k.id}`}>{t("settings.no_cap", "No daily token cap")}{used ? ` · ${used.toLocaleString()} ${t("settings.tokens_today", "tokens today")}` : ""}</p>
            )}
            {auditFor === k.id && auditData && (
              <div className="mt-2 border-t border-gray-100 pt-2" data-testid="apikey-audit-table">
                <p className="text-xs font-semibold text-gray-600 mb-1">{t("settings.last_calls", "Last {n} calls", { n: auditData.rows.length })}</p>
                {auditData.rows.length === 0 && <p className="text-xs text-gray-400">{t("settings.no_calls", "No calls yet.")}</p>}
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {auditData.rows.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                      <span className={r.ok ? "text-green-600" : "text-red-500"}>{r.ok ? "✓" : "✗"}</span>
                      <span className="font-mono">{r.tool || r.scope}</span>
                      {!r.ok && <span className="text-red-400">{r.reason}</span>}
                      <span className="ml-auto">{(r.ts || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );})}
        {keys.length === 0 && <p className="text-sm text-gray-400">{t("settings.no_keys", "No API keys yet.")}</p>}
      </div>

      <WebhooksCard />

      <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
        <div className="text-sm font-semibold text-gray-800 mb-2">{t("settings.quickstart", "Quick start — REST endpoints")}</div>
        <pre className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{`# All calls need header: X-API-Key: <your key>
GET  ${apiBase}/emails?folder=inbox     # list emails
GET  ${apiBase}/contacts               # list contacts
POST ${apiBase}/emails/send            # {"to_email","subject","body"}
POST ${apiBase}/agent/chat             # {"message":"Draft a reply to ..."}

# MCP server (for Claude Desktop / ChatGPT / Cursor — key needs 'mcp' scope)
POST ${process.env.REACT_APP_BACKEND_URL}/api/mcp`}</pre>
      </div>
    </PageShell>
  );
}
